import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, cn, Popover } from '@c2fo/liquidity';
import { InfoCircleIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import Skeleton from '@/enterprise/components/Skeleton';
import HistoryChart from '@/enterprise/features/history/HistoryChart';
import HistoryFilter from '@/enterprise/features/history/HistoryFilter';
import HistoryStatsBar from '@/enterprise/features/history/HistoryStatsBar';
import HistoryTable from '@/enterprise/features/history/HistoryTable';
import RangeHeader from '@/enterprise/features/history/RangeHeader';
import useConsolidatedHistoryData from '@/enterprise/features/history/useConsolidatedHistoryData';
import { dateRanges, getRangeKey } from '@/enterprise/features/history/utils';
import { useReporting } from '@/reporting';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { dateAsIsoString } from '@/utils/dateAsIsoString';
import useUrlState from '@/utils/useUrlState';
import useConsolidatedHistoryFilters from '../data/useConsolidatedHistoryFilters';

// When includeFuturePayDates is true, an end date will not be sent in the request for clear data
export interface ConsolidatedHistoryUrlState {
  endDate: string;
  includeFuturePayDates: boolean;
  limit: number;
  makerIds: number[];
  page: number;
  range: string;
  sort: string;
  startDate: string;
}

const { last30Days: defaultRange } = dateRanges;
const defaultState = {
  endDate: defaultRange.endDate,
  includeFuturePayDates: true,
  limit: 25,
  makerIds: [],
  page: 1,
  range: 'last30Days',
  sort: '-depositDate', // updating fallback from pay date to deposit date now that good funds date project is fully released
  startDate: defaultRange.startDate,
} satisfies ConsolidatedHistoryUrlState;

// Helper function to normalize URL state values to arrays
const normalizeToArray = (value: number[] | undefined, defaultValue: number[]) => {
  if (!value) return defaultValue;
  return Array.isArray(value) ? value : [value];
};

const ConsolidatedHistoryComponent = () => {
  const { t } = useTranslation();
  const { selectedCurrency: currency } = useCurrencyContext();
  const { trackEnterpriseEvent } = useReporting();
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [urlState, setUrlState] = useUrlState<ConsolidatedHistoryUrlState>(defaultState);

  // Use custom hook for history filters and transformation logic
  const { customers, customerMakerIdsMap, divisionToOrgUuidMap, makerOrganizationUuidsFilter } =
    useConsolidatedHistoryFilters({
      makerIdsFilter: normalizeToArray(urlState.makerIds, defaultState.makerIds),
    });

  const params = useMemo(() => {
    const startDate = urlState.startDate ?? defaultRange.startDate;
    const endDate = urlState.endDate ?? defaultRange.endDate;

    // validate range parameter matches dates, derive if invalid
    const isValidRange =
      urlState.range === 'custom' ||
      (urlState.range &&
        urlState.range in dateRanges &&
        dateRanges[urlState.range as keyof typeof dateRanges]?.startDate === startDate &&
        dateRanges[urlState.range as keyof typeof dateRanges]?.endDate === endDate);

    const range = isValidRange && urlState.range ? urlState.range : getRangeKey(startDate, endDate);

    return {
      endDate,
      includeFuturePayDates: urlState.includeFuturePayDates ?? defaultState.includeFuturePayDates,
      limit: urlState.limit ?? defaultState.limit,
      makerIds: normalizeToArray(urlState.makerIds, defaultState.makerIds),
      page: urlState.page ?? defaultState.page,
      range,
      startDate,
      sort: urlState.sort ?? defaultState.sort,
    };
  }, [urlState]);

  const appliedFilters = useMemo(() => {
    return Object.keys(params).reduce((acc, curr) => {
      if (curr === 'makerIds' && params[curr].length > 0) {
        acc += 1;
        return acc;
      }

      return acc;
    }, 1); // always 1 filter for date range
  }, [params]);

  const {
    data: { chartData, hasHistoryOutsideRange, latestPayDate, latestDepositDate, stats, tableData },
    isFetchingChartData,
    isFetchingLatestPayDate,
    isFetchingStats,
    isFetchingTableData,
  } = useConsolidatedHistoryData({ ...params, makerOrganizationUuids: makerOrganizationUuidsFilter });

  const [disableFuturePayDates, setDisableFuturePayDates] = useState(false);

  const clearFilters = () => {
    setUrlState((f) => ({
      ...defaultState,
      includeFuturePayDates: f.includeFuturePayDates,
    }));
  };

  const includeFuturePayDatesText = t('taker.history.includeFutureDepositDates');

  return (
    <>
      <HistoryFilter
        customers={customers}
        filterOpen={filterOpen}
        onCustomersChange={(makerOrganizationUuids) => {
          setUrlState((f) => {
            // Normalize makerIds to always be an array (handles single value from URL)
            const currentIds = normalizeToArray(f.makerIds, defaultState.makerIds);

            // Convert incoming UUIDs back to original IDs for URL state management
            const originalMakerIds = makerOrganizationUuids
              .map((uuid) => {
                // Find the original division ID that maps to this UUID
                for (const [divisionId, orgUuid] of divisionToOrgUuidMap.entries()) {
                  if (orgUuid === uuid) {
                    return divisionId;
                  }
                }
              })
              .filter((id) => id !== undefined);

            const anySelected = originalMakerIds.some((id) => currentIds.includes(id));
            let newMakerIds = currentIds;

            if (anySelected) {
              newMakerIds = currentIds.filter((id) => !originalMakerIds.includes(id));
            } else {
              newMakerIds = [...new Set([...currentIds, ...originalMakerIds])];
            }

            return {
              ...f,
              makerIds: newMakerIds,
              page: 1,
            };
          });
          trackEnterpriseEvent('history-customer::clicked');
        }}
        onDateRangeChange={({ range: { startDate, endDate }, rangeKey }) => {
          const today = dateAsIsoString();
          const includeFuturePayDates = endDate >= today;

          setDisableFuturePayDates(endDate < today);
          setUrlState({ ...urlState, startDate, endDate, range: rangeKey, includeFuturePayDates, page: 1 });
          trackEnterpriseEvent('history-filter::clicked', { startDate, endDate });
        }}
        setFilterOpen={setFilterOpen}
        startDate={params.startDate}
        endDate={params.endDate}
        rangeKey={params.range}
        makerOrganizationUuids={makerOrganizationUuidsFilter}
      />
      <div>
        <div className="flex flex-col gap-4 bg-gray-100 px-4 py-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Button onClick={() => setFilterOpen(true)}>{t('core.filters')}</Button>
              {appliedFilters > 0 && (
                <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-lightBlue-500 text-xs font-medium text-white">
                  {appliedFilters}
                </div>
              )}
            </div>
            <Button onClick={clearFilters} variant="tertiary">
              {t('core.clearFilters')}
            </Button>
          </div>
          <div>
            <Checkbox
              id={'includeFuturePayDates'}
              color="secondary"
              label={
                <span className="flex items-center gap-2 text-sm">
                  <span className={cn({ 'text-text-disabled': disableFuturePayDates })}>
                    {includeFuturePayDatesText}
                  </span>
                  {disableFuturePayDates && (
                    <Popover>
                      <Popover.Trigger>
                        <InfoCircleIcon className="fill-text-secondary" />
                      </Popover.Trigger>
                      <Popover.Content arrow side="bottom">
                        <div className="text-sm">{t('taker.history.includeFuturePayDatesDisabledLabel')}</div>
                      </Popover.Content>
                    </Popover>
                  )}
                </span>
              }
              checked={params.includeFuturePayDates}
              disabled={disableFuturePayDates}
              onChange={(e) => {
                setUrlState({ ...urlState, includeFuturePayDates: e.target.checked });
                trackEnterpriseEvent('history-include-future-pay-dates::clicked', { value: e.target.checked });
              }}
            />
          </div>
        </div>
        <div className="px-4">
          <RangeHeader
            customers={customers}
            endDate={params.endDate}
            isDownloadDisabled={!stats?.clearedInvoiceAmount || isFetchingLatestPayDate || isFetchingStats}
            includeFuturePayDates={params.includeFuturePayDates}
            latestPayDate={latestPayDate}
            latestDepositDate={latestDepositDate}
            selectedCustomers={makerOrganizationUuidsFilter}
            startDate={params.startDate}
            customerMakerIdsMap={customerMakerIdsMap}
            rangeKey={params.range}
          />
          <HistoryStatsBar
            clearedDiscountWeightedAvg={stats?.clearedDiscountWeightedAvg}
            clearedEarn={stats?.clearedEarn}
            clearedInvoiceAmount={stats?.clearedInvoiceAmount}
            currency={currency}
            loading={isFetchingStats}
          />
          <div className="hidden py-4 pt-8 lg:flex">
            <HistoryChart
              currency={currency}
              data={chartData}
              endDate={params.endDate}
              loading={isFetchingChartData}
              startDate={params.startDate}
            />
          </div>
        </div>
        <div className="flex w-full flex-col overflow-hidden">
          <HistoryTable
            data={tableData}
            sort={params.sort}
            setSort={(sort) => {
              setUrlState({ ...urlState, sort, page: 1 });
              trackEnterpriseEvent('history-sort::clicked', { key: sort });
            }}
            pageIndex={params.page}
            pageSize={params.limit}
            onPageChange={(page) => {
              setUrlState({ ...urlState, page });
              trackEnterpriseEvent('history-page::clicked');
            }}
            onPageSizeChange={(limit) => {
              setUrlState({ ...urlState, limit });
              trackEnterpriseEvent('history-page-size::clicked', { size: limit });
            }}
            historyType="consolidated"
            loading={isFetchingTableData}
            hasHistoryOutsideRange={hasHistoryOutsideRange}
            onOpenFilters={() => setFilterOpen(true)}
          />
        </div>
      </div>
    </>
  );
};

const ConsolidatedHistory = () => {
  return (
    <QueryBoundaries
      LoadingComponent={() => (
        <div className="space-y-6 px-4 py-8">
          <Skeleton className="mb-4 h-40 w-full" />
          <Skeleton className="h-[352px] w-full" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="mb-2 h-16 w-full" />
          ))}
        </div>
      )}
    >
      <ConsolidatedHistoryComponent />
    </QueryBoundaries>
  );
};

export default ConsolidatedHistory;
